/**
 * @krisspy-file
 * @type page
 * @name "DesignSystemTest"
 * @title "Design System Test"
 * @description "Demo page showcasing the design system with default theme"
 * @routes ["/design-system"]
 * @design "template"
 * @requiresAuth false
 */

import {
  Package,
  Calendar,
  Users,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Info,
  XCircle
} from 'lucide-react';
import { useState } from 'react';

export default function DesignSystemTest() {
  const [activeTab, setActiveTab] = useState<string>('overview');

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'colors', label: 'Colors' },
    { id: 'typography', label: 'Typography' },
    { id: 'buttons', label: 'Buttons' },
    { id: 'forms', label: 'Forms' },
    { id: 'components', label: 'Components' },
  ];

  return (
    <div className="min-h-screen bg-secondary">
      {/* Header */}
      <header className="bg-primary border-b border-primary shadow-sm">
        <div className="max-w-7xl mx-auto px-xl py-lg">
          <h1 className="text-3xl font-bold text-primary">Rental Admin Panel - Design System</h1>
          <p className="text-secondary mt-sm">Professional theme for rental management</p>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-primary border-b border-primary sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-xl flex gap-md overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-md px-lg border-b-2 transition-all duration-200 whitespace-nowrap text-sm font-medium ${
                activeTab === tab.id
                  ? 'border-blue text-blue'
                  : 'border-transparent text-secondary hover:text-primary hover:border-border'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-xl py-2xl">
        {/* Overview Section */}
        {activeTab === 'overview' && (
          <section className="space-y-2xl animate-fade-in">
            <div>
              <h2 className="text-2xl font-bold mb-md text-primary">Design System Overview</h2>
              <p className="text-secondary">
                This design system provides a consistent, professional interface for the rental booking admin panel.
                It features a clean, modern aesthetic with excellent accessibility.
              </p>
            </div>

            {/* Key Metrics Cards Example */}
            <div>
              <h3 className="text-lg font-semibold mb-lg text-primary">Example: Dashboard Metrics</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-lg">
                <div className="card">
                  <div className="flex items-start justify-between mb-md">
                    <div className="w-10 h-10 rounded-md bg-primary-light flex items-center justify-center">
                      <Package className="w-5 h-5 text-blue" />
                    </div>
                    <span className="badge badge-success">+12%</span>
                  </div>
                  <p className="text-secondary text-sm mb-xs">Active Bookings</p>
                  <p className="text-2xl font-bold text-primary">247</p>
                </div>

                <div className="card">
                  <div className="flex items-start justify-between mb-md">
                    <div className="w-10 h-10 rounded-md bg-success/10 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-success" />
                    </div>
                    <span className="badge badge-info">Today</span>
                  </div>
                  <p className="text-secondary text-sm mb-xs">Revenue</p>
                  <p className="text-2xl font-bold text-primary">$8,492</p>
                </div>

                <div className="card">
                  <div className="flex items-start justify-between mb-md">
                    <div className="w-10 h-10 rounded-md bg-warning/10 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-warning" />
                    </div>
                    <span className="badge badge-warning">Pending</span>
                  </div>
                  <p className="text-secondary text-sm mb-xs">Pending Payments</p>
                  <p className="text-2xl font-bold text-primary">18</p>
                </div>

                <div className="card">
                  <div className="flex items-start justify-between mb-md">
                    <div className="w-10 h-10 rounded-md bg-blue/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue" />
                    </div>
                    <span className="badge badge-info">Active</span>
                  </div>
                  <p className="text-secondary text-sm mb-xs">Total Customers</p>
                  <p className="text-2xl font-bold text-primary">1,284</p>
                </div>
              </div>
            </div>

            {/* Interactive Cards Example */}
            <div>
              <h3 className="text-lg font-semibold mb-lg text-primary">Example: Interactive Product Cards</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-lg">
                <div className="card-interactive">
                  <div className="w-full h-32 bg-tertiary rounded-md mb-md" />
                  <h4 className="font-semibold text-primary mb-xs">Mountain Bike Pro</h4>
                  <p className="text-secondary text-sm mb-md">Premium rental equipment</p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-blue">$45/day</span>
                    <span className="badge badge-success">Available</span>
                  </div>
                </div>

                <div className="card-interactive">
                  <div className="w-full h-32 bg-tertiary rounded-md mb-md" />
                  <h4 className="font-semibold text-primary mb-xs">Kayak Explorer</h4>
                  <p className="text-secondary text-sm mb-md">Single person kayak</p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-blue">$35/day</span>
                    <span className="badge badge-warning">2 Available</span>
                  </div>
                </div>

                <div className="card-interactive">
                  <div className="w-full h-32 bg-tertiary rounded-md mb-md" />
                  <h4 className="font-semibold text-primary mb-xs">Camping Tent</h4>
                  <p className="text-secondary text-sm mb-md">4-person weatherproof</p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-blue">$25/day</span>
                    <span className="badge badge-danger">Rented</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Colors Section */}
        {activeTab === 'colors' && (
          <section className="space-y-2xl animate-fade-in">
            <div>
              <h2 className="text-2xl font-bold mb-md text-primary">Color Palette</h2>
              <p className="text-secondary">Professional color system for the rental admin interface</p>
            </div>

            {/* Brand Colors */}
            <div>
              <h3 className="text-lg font-semibold mb-lg text-primary">Brand Colors</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-lg">
                <div className="space-y-md">
                  <div className="bg-blue h-24 rounded-lg shadow" />
                  <div>
                    <p className="font-semibold text-primary text-sm">Primary</p>
                    <p className="text-secondary text-xs">#3b82f6</p>
                  </div>
                </div>
                <div className="space-y-md">
                  <div className="bg-blue-hover h-24 rounded-lg shadow" />
                  <div>
                    <p className="font-semibold text-primary text-sm">Primary Hover</p>
                    <p className="text-secondary text-xs">#2563eb</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Semantic Colors */}
            <div>
              <h3 className="text-lg font-semibold mb-lg text-primary">Semantic Colors</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-lg">
                <div className="space-y-md">
                  <div className="bg-success h-24 rounded-lg shadow" />
                  <div>
                    <p className="font-semibold text-primary text-sm">Success</p>
                    <p className="text-secondary text-xs">#10b981</p>
                  </div>
                </div>
                <div className="space-y-md">
                  <div className="bg-danger h-24 rounded-lg shadow" />
                  <div>
                    <p className="font-semibold text-primary text-sm">Danger</p>
                    <p className="text-secondary text-xs">#ef4444</p>
                  </div>
                </div>
                <div className="space-y-md">
                  <div className="bg-warning h-24 rounded-lg shadow" />
                  <div>
                    <p className="font-semibold text-primary text-sm">Warning</p>
                    <p className="text-secondary text-xs">#f59e0b</p>
                  </div>
                </div>
                <div className="space-y-md">
                  <div className="h-24 rounded-lg shadow" style={{ backgroundColor: 'var(--info)' }} />
                  <div>
                    <p className="font-semibold text-primary text-sm">Info</p>
                    <p className="text-secondary text-xs">#0ea5e9</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Background Colors */}
            <div>
              <h3 className="text-lg font-semibold mb-lg text-primary">Background Colors</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-lg">
                <div className="space-y-md">
                  <div className="bg-primary border border-primary h-24 rounded-lg shadow" />
                  <div>
                    <p className="font-semibold text-primary text-sm">Primary</p>
                    <p className="text-secondary text-xs">#FFFFFF</p>
                  </div>
                </div>
                <div className="space-y-md">
                  <div className="bg-secondary border border-primary h-24 rounded-lg shadow" />
                  <div>
                    <p className="font-semibold text-primary text-sm">Secondary</p>
                    <p className="text-secondary text-xs">#F9FAFB</p>
                  </div>
                </div>
                <div className="space-y-md">
                  <div className="bg-tertiary border border-primary h-24 rounded-lg shadow" />
                  <div>
                    <p className="font-semibold text-primary text-sm">Tertiary</p>
                    <p className="text-secondary text-xs">#F3F4F6</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Typography Section */}
        {activeTab === 'typography' && (
          <section className="space-y-2xl animate-fade-in">
            <div>
              <h2 className="text-2xl font-bold mb-md text-primary">Typography</h2>
              <p className="text-secondary">System font stack for optimal readability</p>
            </div>

            <div className="space-y-lg">
              <div className="text-4xl font-bold text-primary">Display - 36px Bold</div>
              <div className="text-3xl font-bold text-primary">Heading 1 - 30px Bold</div>
              <div className="text-2xl font-bold text-primary">Heading 2 - 24px Bold</div>
              <div className="text-xl font-semibold text-primary">Heading 3 - 20px Semibold</div>
              <div className="text-lg font-semibold text-primary">Heading 4 - 18px Semibold</div>
              <div className="text-base text-primary">Body - 16px Regular</div>
              <div className="text-sm text-primary">Small - 14px Regular</div>
              <div className="text-xs text-secondary">Extra Small - 12px Regular</div>
            </div>
          </section>
        )}

        {/* Buttons Section */}
        {activeTab === 'buttons' && (
          <section className="space-y-2xl animate-fade-in">
            <div>
              <h2 className="text-2xl font-bold mb-md text-primary">Buttons</h2>
              <p className="text-secondary">Button variants and states</p>
            </div>

            {/* Primary Buttons */}
            <div>
              <h3 className="text-lg font-semibold mb-lg text-primary">Primary Buttons</h3>
              <div className="flex flex-wrap gap-md">
                <button className="btn btn-primary">
                  <Package className="w-4 h-4" />
                  Create Booking
                </button>
                <button className="btn btn-primary">Save Changes</button>
                <button className="btn btn-primary" disabled>Disabled</button>
              </div>
            </div>

            {/* Secondary Buttons */}
            <div>
              <h3 className="text-lg font-semibold mb-lg text-primary">Secondary Buttons</h3>
              <div className="flex flex-wrap gap-md">
                <button className="btn btn-secondary">
                  <Calendar className="w-4 h-4" />
                  Check Availability
                </button>
                <button className="btn btn-secondary">Cancel</button>
                <button className="btn btn-secondary" disabled>Disabled</button>
              </div>
            </div>

            {/* Ghost Buttons */}
            <div>
              <h3 className="text-lg font-semibold mb-lg text-primary">Ghost Buttons</h3>
              <div className="flex flex-wrap gap-md">
                <button className="btn btn-ghost">View Details</button>
                <button className="btn btn-ghost">Export</button>
                <button className="btn btn-ghost" disabled>Disabled</button>
              </div>
            </div>

            {/* Button Sizes */}
            <div>
              <h3 className="text-lg font-semibold mb-lg text-primary">Button Combinations</h3>
              <div className="flex flex-wrap gap-md items-center">
                <button className="btn btn-primary">
                  <CheckCircle className="w-4 h-4" />
                  Confirm
                </button>
                <button className="btn btn-secondary">
                  <XCircle className="w-4 h-4" />
                  Cancel
                </button>
                <button className="btn btn-ghost">
                  <Info className="w-4 h-4" />
                  More Info
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Forms Section */}
        {activeTab === 'forms' && (
          <section className="space-y-2xl animate-fade-in">
            <div>
              <h2 className="text-2xl font-bold mb-md text-primary">Form Elements</h2>
              <p className="text-secondary">Input fields and form controls</p>
            </div>

            <div className="max-w-2xl space-y-xl">
              {/* Text Input */}
              <div>
                <label className="block text-sm font-medium mb-sm text-primary">
                  Customer Name
                </label>
                <input
                  type="text"
                  placeholder="Enter customer name"
                  className="input"
                />
              </div>

              {/* Email Input */}
              <div>
                <label className="block text-sm font-medium mb-sm text-primary">
                  Email Address
                </label>
                <input
                  type="email"
                  placeholder="customer@example.com"
                  className="input"
                />
              </div>

              {/* Select */}
              <div>
                <label className="block text-sm font-medium mb-sm text-primary">
                  Product Category
                </label>
                <select className="input">
                  <option>Select a category</option>
                  <option>Bikes & Cycling</option>
                  <option>Water Sports</option>
                  <option>Camping Equipment</option>
                  <option>Winter Sports</option>
                </select>
              </div>

              {/* Textarea */}
              <div>
                <label className="block text-sm font-medium mb-sm text-primary">
                  Notes
                </label>
                <textarea
                  placeholder="Add any additional notes..."
                  className="input resize-none"
                  rows={4}
                />
              </div>

              {/* Checkbox */}
              <div>
                <label className="flex items-start gap-md cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 w-4 h-4 rounded border-primary accent-blue cursor-pointer"
                  />
                  <div>
                    <p className="text-sm font-medium text-primary">Send confirmation email</p>
                    <p className="text-xs text-secondary">Customer will receive booking details via email</p>
                  </div>
                </label>
              </div>

              {/* Radio Buttons */}
              <div>
                <p className="text-sm font-medium mb-md text-primary">Payment Status</p>
                <div className="space-y-sm">
                  <label className="flex items-center gap-md cursor-pointer">
                    <input
                      type="radio"
                      name="payment"
                      className="w-4 h-4 border-primary accent-blue cursor-pointer"
                    />
                    <span className="text-sm text-primary">Paid</span>
                  </label>
                  <label className="flex items-center gap-md cursor-pointer">
                    <input
                      type="radio"
                      name="payment"
                      className="w-4 h-4 border-primary accent-blue cursor-pointer"
                    />
                    <span className="text-sm text-primary">Pending</span>
                  </label>
                  <label className="flex items-center gap-md cursor-pointer">
                    <input
                      type="radio"
                      name="payment"
                      className="w-4 h-4 border-primary accent-blue cursor-pointer"
                    />
                    <span className="text-sm text-primary">Open</span>
                  </label>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Components Section */}
        {activeTab === 'components' && (
          <section className="space-y-2xl animate-fade-in">
            <div>
              <h2 className="text-2xl font-bold mb-md text-primary">Components</h2>
              <p className="text-secondary">Reusable UI components</p>
            </div>

            {/* Badges */}
            <div>
              <h3 className="text-lg font-semibold mb-lg text-primary">Status Badges</h3>
              <div className="flex flex-wrap gap-md">
                <span className="badge badge-success">Available</span>
                <span className="badge badge-info">Confirmed</span>
                <span className="badge badge-warning">Pending</span>
                <span className="badge badge-danger">Cancelled</span>
              </div>
            </div>

            {/* Alert Messages */}
            <div>
              <h3 className="text-lg font-semibold mb-lg text-primary">Alert Messages</h3>
              <div className="space-y-md max-w-2xl">
                <div className="bg-success/10 border border-success/30 rounded-lg p-lg flex gap-md">
                  <CheckCircle className="w-5 h-5 text-success flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-success text-sm">Success</p>
                    <p className="text-sm text-success/90">Booking created successfully</p>
                  </div>
                </div>
                <div className="bg-blue/10 border border-blue/30 rounded-lg p-lg flex gap-md">
                  <Info className="w-5 h-5 text-blue flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-blue text-sm">Information</p>
                    <p className="text-sm text-blue/90">Customer will receive a confirmation email</p>
                  </div>
                </div>
                <div className="bg-warning/10 border border-warning/30 rounded-lg p-lg flex gap-md">
                  <AlertCircle className="w-5 h-5 text-warning flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-warning text-sm">Warning</p>
                    <p className="text-sm text-warning/90">Limited availability for selected dates</p>
                  </div>
                </div>
                <div className="bg-danger/10 border border-danger/30 rounded-lg p-lg flex gap-md">
                  <XCircle className="w-5 h-5 text-danger flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-danger text-sm">Error</p>
                    <p className="text-sm text-danger/90">Failed to process payment</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Data Table Example */}
            <div>
              <h3 className="text-lg font-semibold mb-lg text-primary">Data Table</h3>
              <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-secondary border-b border-primary">
                      <tr>
                        <th className="text-left py-md px-lg text-sm font-semibold text-primary">Booking ID</th>
                        <th className="text-left py-md px-lg text-sm font-semibold text-primary">Customer</th>
                        <th className="text-left py-md px-lg text-sm font-semibold text-primary">Product</th>
                        <th className="text-left py-md px-lg text-sm font-semibold text-primary">Status</th>
                        <th className="text-left py-md px-lg text-sm font-semibold text-primary">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      <tr className="hover:bg-secondary transition-colors">
                        <td className="py-md px-lg text-sm text-primary">#BK-001</td>
                        <td className="py-md px-lg text-sm text-primary">John Smith</td>
                        <td className="py-md px-lg text-sm text-secondary">Mountain Bike</td>
                        <td className="py-md px-lg text-sm">
                          <span className="badge badge-success">Confirmed</span>
                        </td>
                        <td className="py-md px-lg text-sm font-medium text-primary">$180</td>
                      </tr>
                      <tr className="hover:bg-secondary transition-colors">
                        <td className="py-md px-lg text-sm text-primary">#BK-002</td>
                        <td className="py-md px-lg text-sm text-primary">Sarah Johnson</td>
                        <td className="py-md px-lg text-sm text-secondary">Kayak</td>
                        <td className="py-md px-lg text-sm">
                          <span className="badge badge-warning">Pending</span>
                        </td>
                        <td className="py-md px-lg text-sm font-medium text-primary">$105</td>
                      </tr>
                      <tr className="hover:bg-secondary transition-colors">
                        <td className="py-md px-lg text-sm text-primary">#BK-003</td>
                        <td className="py-md px-lg text-sm text-primary">Mike Davis</td>
                        <td className="py-md px-lg text-sm text-secondary">Camping Tent</td>
                        <td className="py-md px-lg text-sm">
                          <span className="badge badge-info">Active</span>
                        </td>
                        <td className="py-md px-lg text-sm font-medium text-primary">$75</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Empty State */}
            <div>
              <h3 className="text-lg font-semibold mb-lg text-primary">Empty State</h3>
              <div className="card text-center py-2xl">
                <div className="w-16 h-16 rounded-full bg-tertiary mx-auto mb-lg flex items-center justify-center">
                  <Package className="w-8 h-8 text-secondary" />
                </div>
                <h4 className="text-lg font-semibold text-primary mb-sm">No bookings yet</h4>
                <p className="text-secondary text-sm mb-xl">
                  Get started by creating your first booking
                </p>
                <button className="btn btn-primary">
                  <Package className="w-4 h-4" />
                  Create Booking
                </button>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-primary bg-primary mt-2xl">
        <div className="max-w-7xl mx-auto px-xl py-xl text-center">
          <p className="text-secondary text-sm">
            Rental Admin Panel Design System - Professional Theme
          </p>
        </div>
      </footer>
    </div>
  );
}
